import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { ArrowLeft, AlertCircle, Plus, Check } from 'lucide-react';
import { useGameStore } from '@/lib/gameStore';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { validateRoomCode } from '@/lib/validationSchemas';
import { mapDatabaseError } from '@/lib/errorUtils';

interface JoinRoomProps {
  onNavigate: (screen: 'menu' | 'lobby') => void;
  initialRoomCode?: string;
}

export default function JoinRoom({ onNavigate, initialRoomCode }: JoinRoomProps) {
  const { playerName, setPlayerId, setCurrentRoom } = useGameStore();
  const [roomCode, setRoomCode] = useState(initialRoomCode?.toUpperCase() || '');
  const [isJoining, setIsJoining] = useState(false);
  const [roomNotFound, setRoomNotFound] = useState(false);
  const { toast } = useToast();

  const isCodeReady = roomCode.length === 6;

  const handleJoinRoom = async () => {
    // Clear previous error
    setRoomNotFound(false);
    
    // Validate room code
    const codeError = validateRoomCode(roomCode);
    if (codeError) {
      toast({ title: "Invalid room code", description: codeError, variant: "destructive" });
      return;
    }

    setIsJoining(true);
    try {
      const code = roomCode.toUpperCase();

      // Check if room exists
      const { data: room, error: roomError } = await supabase
        .from('rooms')
        .select('*')
        .eq('id', code)
        .single();

      if (roomError || !room) {
        setRoomNotFound(true);
        setIsJoining(false);
        return;
      }

      if (room.status !== 'lobby') {
        throw new Error("Game already in progress!");
      }

      // Get existing players
      const { data: existingPlayers } = await supabase
        .from('players')
        .select('*')
        .eq('room_id', code);

      if (existingPlayers && existingPlayers.length >= 8) {
        throw new Error("Room is full!");
      }

      // Sign in anonymously to get auth_user_id
      const { data: authData, error: authError } = await supabase.auth.signInAnonymously();
      if (authError) throw authError;

      // Create player with auth_user_id
      const { data: player, error: playerError } = await supabase
        .from('players')
        .insert({
          room_id: code,
          name: playerName,
          is_host: false,
          auth_user_id: authData.user.id,
        })
        .select()
        .single();

      if (playerError) throw playerError;

      setPlayerId(player.id);
      
      // Map players to the expected format
      const players = existingPlayers?.map(p => ({
        id: p.id,
        name: p.name,
        isHost: p.is_host,
        stressLevel: p.stress_level as any,
        isActive: p.is_active,
      })) || [];

      setCurrentRoom({
        id: code,
        hostId: room.host_id,
        status: 'lobby',
        currentRound: 0,
        currentTotal: 0,
        targetTotal: 67,
        players: [
          ...players,
          { 
            id: player.id, 
            name: playerName, 
            isHost: false, 
            stressLevel: 'calm',
            isActive: true 
          }
        ],
        hasSelected: false,
        roundTimeRemaining: 3,
      });

      onNavigate('lobby');
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error('Error joining room (dev only):', error);
      }
      toast({ 
        title: "Failed to join room", 
        description: mapDatabaseError(error), 
        variant: "destructive" 
      });
    } finally {
      setIsJoining(false);
    }
  };

  const handleCreateRoomInstead = () => {
    onNavigate('menu');
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-background via-card to-background" />
      
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md space-y-6"
      >
        <div className="text-center">
          <h1 className="text-5xl font-black uppercase tracking-tighter text-shadow mb-2">
            <span className="text-primary">Join</span> Game
          </h1>
        </div>

        <Card className="glass-card p-6 space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-bold uppercase tracking-wide text-muted-foreground">
              Room Code (6 characters)
            </label>
            <Input
              value={roomCode}
              onChange={(e) => {
                const value = e.target.value.toUpperCase().slice(0, 6);
                // Only allow alphanumeric characters
                const filtered = value.replace(/[^A-Z0-9]/g, '');
                setRoomCode(filtered);
                // Clear error when user starts typing again
                if (roomNotFound) setRoomNotFound(false);
              }}
              placeholder="e.g., ABC123"
              className={`bg-secondary/50 border-2 h-14 text-2xl font-black text-center tracking-widest uppercase transition-colors ${
                roomNotFound 
                  ? 'border-destructive focus:border-destructive' 
                  : 'border-border/50'
              }`}
              maxLength={6}
            />
            
            {/* Character counter */}
            <div className="flex items-center justify-end gap-2 text-sm">
              <span className={`font-semibold transition-colors ${
                isCodeReady ? 'text-primary' : 'text-muted-foreground'
              }`}>
                {roomCode.length}/6 characters
              </span>
              {isCodeReady && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="flex items-center gap-1 text-primary font-bold"
                >
                  <Check className="h-4 w-4" />
                  Ready
                </motion.span>
              )}
            </div>
          </div>

          {/* Room Not Found Error Card */}
          <AnimatePresence>
            {roomNotFound && (
              <motion.div
                initial={{ opacity: 0, height: 0, y: -10 }}
                animate={{ opacity: 1, height: 'auto', y: 0 }}
                exit={{ opacity: 0, height: 0, y: -10 }}
                className="overflow-hidden"
              >
                <Card className="bg-destructive/10 border-destructive/30 p-4 space-y-3">
                  <div className="flex items-center gap-2 text-destructive">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-bold">Room Not Found</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    No room exists with code <span className="font-bold text-foreground">{roomCode}</span>. 
                    Check the code and try again, or create a new room.
                  </p>
                  <Button
                    onClick={handleCreateRoomInstead}
                    variant="outline"
                    className="w-full font-bold border-primary/50 hover:bg-primary/10"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create Room Instead
                  </Button>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="grid gap-3 pt-2">
            <Button
              onClick={handleJoinRoom}
              disabled={isJoining || !isCodeReady}
              className="h-16 text-lg font-bold uppercase tracking-wide glow-green hover:scale-105 transition-transform"
            >
              {isJoining ? 'Joining...' : 'Join Game'}
            </Button>

            <Button
              onClick={() => onNavigate('menu')}
              variant="secondary"
              className="h-12 font-bold"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </div>
        </Card>
      </motion.div>
    </div>
  );
}